import { useEffect, useState } from 'react';

/**
 * The one route this app reads from the address bar.
 *
 * A sign-in link has to be a real URL somebody can open from an email client,
 * so it cannot live behind a hash the way the dashboard's sections do. Only
 * that one path is parsed; everything else is one screen chosen by who is
 * signed in, which is all this ticket needs.
 */
export function signInTokenFromUrl(): string | undefined {
  const match = /^\/sign-in\/(.+)$/.exec(window.location.pathname);
  return match ? decodeURIComponent(match[1]!) : undefined;
}

/**
 * Takes the token out of the address bar once it has been spent.
 *
 * A one-time credential should not sit in the URL where it can be shared,
 * bookmarked or read out of the history by the next person on the device --
 * even a spent one, which still says who was signing in.
 */
export function forgetSignInToken(): void {
  window.history.replaceState({}, '', '/');
  /*
   * `replaceState` changes the address bar and tells nobody.
   *
   * It fires no event, so anything deriving its state from `location` keeps
   * the old value: the first version of this left the app rendering "signing
   * you in" forever, because the token was spent and gone from the URL while
   * the component reading the URL still believed it was there. The event is
   * dispatched by hand so there is one source of truth rather than two that
   * disagree.
   */
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Re-renders when the browser moves, so Back out of a link is not a dead page. */
export function usePathname(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return path;
}

/**
 * Moves to one of the app's three screens.
 *
 * A real path rather than a tab in component state, because somebody who has
 * just accepted somebody else will reload the page, press Back, or send
 * themselves the link -- and a tab that lives only in memory loses all three.
 * `pushState` has the same silence as `replaceState`, so the event is
 * dispatched by hand here too.
 */
export function navigate(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
